import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, Typography, Chip, InputAdornment, Tooltip, Rating,
  FormControl, InputLabel, Select, Stack, Divider, Tabs, Tab, List, ListItem,
  ListItemText, ListItemSecondaryAction, LinearProgress, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, Edit, Search, Delete, Verified, OpenInNew, Visibility,
  AttachFile, CloudUpload, Description, Inventory2, Save,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const STATUS_OPTIONS = [
  { v: 'prospect',          label: 'Prospect',           color: 'default' },
  { v: 'contacted',         label: 'Contacted',          color: 'info' },
  { v: 'samples_requested', label: 'Samples Requested',  color: 'primary' },
  { v: 'samples_received',  label: 'Samples Received',   color: 'secondary' },
  { v: 'evaluating',        label: 'Evaluating',         color: 'warning' },
  { v: 'approved',          label: 'Approved',           color: 'success' },
  { v: 'rejected',          label: 'Rejected',           color: 'error' },
  { v: 'on_hold',           label: 'On Hold',            color: 'default' },
];
const SOURCE_OPTIONS = [
  { v: 'alibaba',        label: 'Alibaba' },
  { v: 'made_in_china',  label: 'Made-in-China' },
  { v: 'referral',       label: 'Referral' },
  { v: 'tradeshow',      label: 'Trade Show' },
  { v: 'google',         label: 'Google Search' },
  { v: 'linkedin',       label: 'LinkedIn' },
  { v: 'other',          label: 'Other' },
];
const COUNTRIES = ['China', 'Hong Kong', 'Taiwan', 'Vietnam', 'India', 'South Korea', 'Japan', 'USA', 'Germany', 'UAE', 'Singapore', 'Other'];

const statusChip = (s) => {
  const o = STATUS_OPTIONS.find(x => x.v === s);
  return <Chip size="small" label={o?.label || s} color={o?.color || 'default'} variant={o?.color === 'default' ? 'outlined' : 'filled'} />;
};

const EMPTY = {
  company_name: '', contact_person: '', email: '', phone: '',
  website: '', wechat_id: '', alibaba_url: '',
  country: 'China', city: '',
  product_categories: '', source: 'alibaba', status: 'prospect',
  rating: '', notes: '', strengths: '', weaknesses: '',
};

// Open a file via the authenticated download endpoint
const openSupplierFile = async (fileId) => {
  try {
    const res = await api.get(`/sourcing-suppliers/files/${fileId}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch { alert('Unable to open file'); }
};

const qty = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n/1024).toFixed(1)} KB`;
  return `${(n/(1024*1024)).toFixed(2)} MB`;
};

export default function SupplierDiscovery() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [list, setList]       = useState([]);
  const [stats, setStats]     = useState(null);
  const [search, setSearch]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState(0);
  // Products & Files (loaded when editing)
  const [products, setProducts]       = useState([]);
  const [productDraft, setProductDraft] = useState(null); // null or product obj
  const [files, setFiles]             = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [fileDescription, setFileDescription] = useState('');
  // (View opens a full page now; no modal state needed)

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search)        params.append('search', search);
      if (filterStatus)  params.append('status', filterStatus);
      if (filterCountry) params.append('country', filterCountry);
      const r = await api.get(`/sourcing-suppliers?${params}`);
      setList(r.data.data || []);
      const s = await api.get('/sourcing-suppliers/stats');
      setStats(s.data);
    } catch (e) { console.error(e); }
  }, [search, filterStatus, filterCountry]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !list.length) return;
    const found = list.find(s => String(s.id) === String(editId));
    if (found) {
      openEdit(found);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

  }, [list]);

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setError(''); setTab(0);
    setProducts([]); setFiles([]); setProductDraft(null);
    setDialog(true);
  };
  const openEdit = async (s) => {
    setEditing(s); setForm({ ...EMPTY, ...s, rating: s.rating || '' });
    setError(''); setTab(0);
    try {
      const [p, f] = await Promise.all([
        api.get(`/sourcing-suppliers/${s.id}/products`),
        api.get(`/sourcing-suppliers/${s.id}/files`),
      ]);
      setProducts(p.data.data || []);
      setFiles(f.data.data || []);
    } catch { setProducts([]); setFiles([]); }
    setProductDraft(null);
    setDialog(true);
  };

  // ── View (full page) ─────────────────────────────────────────────────────
  const openView = (s) => navigate(`/sourcing/discover/${s.id}`);

  // ── Products handlers ────────────────────────────────────────────────────
  const newProduct = () => setProductDraft({ product_name: '', specifications: '', moq: '', unit_of_measure: 'pcs', unit_price: '', currency: 'USD', lead_time_days: '', sample_available: 0, notes: '' });
  const editProduct = (p) => setProductDraft({ ...p });
  const saveProduct = async () => {
    if (!productDraft?.product_name) { alert('Product name required'); return; }
    const payload = { ...productDraft };
    ['moq','unit_price','lead_time_days'].forEach(k => {
      if (payload[k] === '' || payload[k] == null) payload[k] = null;
      else payload[k] = k === 'lead_time_days' ? parseInt(payload[k]) : parseFloat(payload[k]);
    });
    try {
      if (productDraft.id) await api.put(`/sourcing-suppliers/products/${productDraft.id}`, payload);
      else                 await api.post(`/sourcing-suppliers/${editing.id}/products`, payload);
      const p = await api.get(`/sourcing-suppliers/${editing.id}/products`);
      setProducts(p.data.data || []);
      setProductDraft(null);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove "${p.product_name}"?`)) return;
    await api.delete(`/sourcing-suppliers/products/${p.id}`);
    setProducts(products.filter(x => x.id !== p.id));
  };

  // ── File handlers ────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing?.id) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (fileDescription) fd.append('description', fileDescription);
    try {
      await api.post(`/sourcing-suppliers/${editing.id}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const f = await api.get(`/sourcing-suppliers/${editing.id}/files`);
      setFiles(f.data.data || []);
      setFileDescription('');
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };
  const deleteFile = async (f) => {
    if (!window.confirm(`Remove "${f.original_name}"?`)) return;
    await api.delete(`/sourcing-suppliers/files/${f.id}`);
    setFiles(files.filter(x => x.id !== f.id));
  };

  const handleSave = async () => {
    if (!form.company_name) { setError('Company name is required'); return; }
    setLoading(true);
    try {
      const payload = { ...form, rating: form.rating ? parseInt(form.rating) : null };
      if (editing) await api.put(`/sourcing-suppliers/${editing.id}`, payload);
      else         await api.post('/sourcing-suppliers', payload);
      setDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    } finally { setLoading(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove "${s.company_name}"?`)) return;
    await api.delete(`/sourcing-suppliers/${s.id}`);
    load();
  };

  const handlePromote = async (s) => {
    if (!window.confirm(`Promote "${s.company_name}" to active beneficiary?`)) return;
    try {
      await api.post(`/sourcing-suppliers/${s.id}/promote`, { country: s.country, currency: 'USD' });
      load();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const f = (field) => ({ value: form[field] || '', onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box>
      <PageHeader title="Supplier Discovery" subtitle="Track potential new suppliers being evaluated"
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Supplier</Button>}
      />

      {/* Status summary chips */}
      {stats && (
        <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>Pipeline:</Typography>
              <Chip label={`Total: ${stats.total}`} size="small" color="primary" />
              {stats.by_status.map(s => (
                <Chip key={s.status} size="small"
                  label={`${STATUS_OPTIONS.find(x => x.v === s.status)?.label || s.status}: ${s.c}`}
                  color={STATUS_OPTIONS.find(x => x.v === s.status)?.color || 'default'}
                  variant="outlined" />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search company, products, contact…"
          value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Country</InputLabel>
          <Select value={filterCountry} label="Country" onChange={e => setFilterCountry(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {isMobile ? (
        list.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No suppliers in pipeline. Tap "Add Supplier" to start.
          </Paper>
        ) : (
          <Stack spacing={1.25}>
            {list.map(s => (
              <Paper key={s.id} elevation={0}
                onClick={() => openView(s)}
                sx={{ p: 1.75, border: '1px solid', borderColor: 'divider',
                  cursor: 'pointer', touchAction: 'manipulation',
                  '&:active': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.company_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[s.country, s.city].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </Box>
                  {statusChip(s.status)}
                </Box>
                {s.product_categories && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.product_categories}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.5, mt: 0.5 }}>
                  <Chip size="small" variant="outlined" label={SOURCE_OPTIONS.find(x => x.v === s.source)?.label || s.source} />
                  {s.rating ? <Rating value={s.rating} readOnly size="small" /> : null}
                  {s.approved_beneficiary_id && <Chip size="small" icon={<Verified />} color="success" variant="outlined" label="Promoted" />}
                </Stack>
                {(s.contact_person || s.email || s.phone) && (
                  <Box sx={{ mt: 0.75, fontSize: 11, color: 'text.secondary' }}>
                    {s.contact_person && <Box>{s.contact_person}</Box>}
                    {s.email && <Box>{s.email}</Box>}
                    {s.phone && <Box>{s.phone}</Box>}
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 0.25 }} onClick={e => e.stopPropagation()}>
                  <IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton>
                  {s.status === 'approved' && !s.approved_beneficiary_id && (
                    <IconButton size="small" color="success" onClick={() => handlePromote(s)}><Verified fontSize="small" /></IconButton>
                  )}
                  <IconButton size="small" color="error" onClick={() => handleDelete(s)}><Delete fontSize="small" /></IconButton>
                </Box>
              </Paper>
            ))}
          </Stack>
        )
      ) : (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
              <TableCell>Company</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Rating</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                No suppliers in pipeline. Add your first prospect.
              </TableCell></TableRow>
            ) : list.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.company_name}</Typography>
                  {s.alibaba_url && <a href={s.alibaba_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1976d2' }}><OpenInNew sx={{ fontSize: 12, mr: 0.3 }}/>Alibaba</a>}
                  {s.website && <a href={s.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1976d2', marginLeft: 8 }}><OpenInNew sx={{ fontSize: 12, mr: 0.3 }}/>Web</a>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize={12}>{s.contact_person || '—'}</Typography>
                  {s.email && <Typography variant="caption" color="text.secondary" display="block">{s.email}</Typography>}
                  {s.phone && <Typography variant="caption" color="text.secondary" display="block">{s.phone}</Typography>}
                </TableCell>
                <TableCell><Typography variant="body2">{s.country || '—'}{s.city ? `, ${s.city}` : ''}</Typography></TableCell>
                <TableCell sx={{ maxWidth: 220, fontSize: 12 }}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.product_categories || '—'}</Typography>
                </TableCell>
                <TableCell><Chip label={SOURCE_OPTIONS.find(x => x.v === s.source)?.label || s.source} size="small" variant="outlined" /></TableCell>
                <TableCell>{statusChip(s.status)}</TableCell>
                <TableCell align="center">
                  {s.rating ? <Rating value={s.rating} readOnly size="small" /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details"><IconButton size="small" color="info" onClick={() => openView(s)}><Visibility fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                  {s.status === 'approved' && !s.approved_beneficiary_id && (
                    <Tooltip title="Promote to Beneficiary"><IconButton size="small" color="success" onClick={() => handlePromote(s)}><Verified fontSize="small" /></IconButton></Tooltip>
                  )}
                  {s.approved_beneficiary_id && (
                    <Tooltip title={`Promoted: ${s.approved_beneficiary_name || ''}`}><Verified sx={{ color: 'success.main', fontSize: 18, ml: 1 }} /></Tooltip>
                  )}
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(s)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialog}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setDialog(false); }}
        fullWidth maxWidth="md">
        <DialogTitle>{editing ? `Edit: ${editing.company_name}` : 'Add Potential Supplier'}</DialogTitle>
        {editing && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab label="Details" />
            <Tab label={`Products${products.length ? ` (${products.length})` : ''}`} icon={<Inventory2 fontSize="small" />} iconPosition="start" />
            <Tab label={`Files${files.length ? ` (${files.length})` : ''}`} icon={<AttachFile fontSize="small" />} iconPosition="start" />
          </Tabs>
        )}
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Company Name" {...f('company_name')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Contact Person" {...f('contact_person')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email" {...f('email')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Phone" {...f('phone')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Website URL" {...f('website')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Alibaba / Listing URL" {...f('alibaba_url')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="WeChat ID" {...f('wechat_id')} /></Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Country" {...f('country')}>
                {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="City" {...f('city')} /></Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Source" {...f('source')}>
                {SOURCE_OPTIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Product Categories (comma separated)" placeholder="RFID tags, smart cards, NFC stickers" {...f('product_categories')} /></Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Status" {...f('status')}>
                {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Rating</Typography>
                <Box><Rating value={parseInt(form.rating) || 0}
                  onChange={(_, v) => setForm({ ...form, rating: v || '' })} /></Box>
              </Box>
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Evaluation</Typography></Divider></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth multiline rows={2} label="Strengths" {...f('strengths')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth multiline rows={2} label="Weaknesses / Concerns" {...f('weaknesses')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Notes" {...f('notes')} /></Grid>
          </Grid>
          )}

          {/* Products tab */}
          {tab === 1 && editing && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Products this supplier can manufacture / supply.
                </Typography>
                <Button size="small" variant="contained" startIcon={<Add />} onClick={newProduct} disabled={!!productDraft}>
                  Add Product
                </Button>
              </Box>

              {productDraft && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {productDraft.id ? 'Edit Product' : 'New Product'}
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}><TextField size="small" fullWidth required label="Product Name"
                      value={productDraft.product_name || ''}
                      onChange={e => setProductDraft({ ...productDraft, product_name: e.target.value })} /></Grid>
                    <Grid item xs={6} md={3}><TextField size="small" fullWidth type="number" label="MOQ"
                      value={productDraft.moq || ''}
                      onChange={e => setProductDraft({ ...productDraft, moq: e.target.value })} /></Grid>
                    <Grid item xs={6} md={3}>
                      <TextField select size="small" fullWidth label="UOM"
                        value={productDraft.unit_of_measure || 'pcs'}
                        onChange={e => setProductDraft({ ...productDraft, unit_of_measure: e.target.value })}>
                        {['pcs','kg','g','m','roll','box','set'].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}><TextField size="small" fullWidth type="number" label="Unit Price"
                      value={productDraft.unit_price || ''}
                      onChange={e => setProductDraft({ ...productDraft, unit_price: e.target.value })} /></Grid>
                    <Grid item xs={6} md={3}>
                      <TextField select size="small" fullWidth label="Currency"
                        value={productDraft.currency || 'USD'}
                        onChange={e => setProductDraft({ ...productDraft, currency: e.target.value })}>
                        {['USD','EUR','CNY','HKD'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}><TextField size="small" fullWidth type="number" label="Lead Time (days)"
                      value={productDraft.lead_time_days || ''}
                      onChange={e => setProductDraft({ ...productDraft, lead_time_days: e.target.value })} /></Grid>
                    <Grid item xs={6} md={3}>
                      <TextField select size="small" fullWidth label="Sample Available"
                        value={productDraft.sample_available ? 1 : 0}
                        onChange={e => setProductDraft({ ...productDraft, sample_available: parseInt(e.target.value) })}>
                        <MenuItem value={0}>No</MenuItem>
                        <MenuItem value={1}>Yes</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Specifications"
                      value={productDraft.specifications || ''}
                      onChange={e => setProductDraft({ ...productDraft, specifications: e.target.value })} /></Grid>
                    <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Notes"
                      value={productDraft.notes || ''}
                      onChange={e => setProductDraft({ ...productDraft, notes: e.target.value })} /></Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setProductDraft(null)}>Cancel</Button>
                    <Button size="small" variant="contained" startIcon={<Save />} onClick={saveProduct}>Save Product</Button>
                  </Box>
                </Paper>
              )}

              {products.length === 0 && !productDraft ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No products listed yet. Click "Add Product" to start.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">MOQ</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Lead Time</TableCell>
                        <TableCell align="center">Sample</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map(p => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{p.product_name}</Typography>
                            {p.specifications && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.specifications}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{p.moq ? `${qty(p.moq)} ${p.unit_of_measure || ''}` : '—'}</TableCell>
                          <TableCell align="right">{p.unit_price ? `${p.currency || ''} ${p.unit_price}` : '—'}</TableCell>
                          <TableCell align="right">{p.lead_time_days ? `${p.lead_time_days} d` : '—'}</TableCell>
                          <TableCell align="center">
                            {p.sample_available ? <Chip size="small" label="Yes" color="success" /> : <Chip size="small" label="No" variant="outlined" />}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => editProduct(p)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteProduct(p)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Files tab */}
          {tab === 2 && editing && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload quotes, sample photos, certifications, business licenses, etc. (max 25 MB per file).
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                  <TextField size="small" label="Description (optional)" placeholder="Quote v1, Sample photo, Business license…"
                    value={fileDescription} onChange={e => setFileDescription(e.target.value)} sx={{ flex: 1 }} />
                  <Button component="label" variant="contained" startIcon={<CloudUpload />} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Upload File'}
                    <input type="file" hidden onChange={handleFileUpload} />
                  </Button>
                </Stack>
                {uploading && <LinearProgress sx={{ mt: 1 }} />}
              </Paper>

              {files.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No files uploaded yet.
                </Typography>
              ) : (
                <List dense>
                  {files.map(f => (
                    <ListItem key={f.id} divider sx={{ pr: 12 }}>
                      <Description sx={{ mr: 1.5, color: 'action.active' }} />
                      <ListItemText
                        primary={
                          <Box component="span" onClick={() => openSupplierFile(f.id)}
                            sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                            {f.original_name}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ fontSize: 11 }}>
                            {f.description ? `${f.description} · ` : ''}
                            {formatBytes(f.size_bytes)} · {f.uploaded_by_name || 'Unknown'} · {f.created_at?.substring(0, 10)}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Open"><IconButton size="small"
                          onClick={() => openSupplierFile(f.id)}><OpenInNew fontSize="small" /></IconButton></Tooltip>
                        <IconButton size="small" color="error" onClick={() => deleteFile(f)}><Delete fontSize="small" /></IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Close</Button>
          {tab === 0 && <Button variant="contained" onClick={handleSave} disabled={loading}>{editing ? 'Update' : 'Add'}</Button>}
        </DialogActions>
      </Dialog>

    </Box>
  );
}
